// Tier 1 federated training artifacts loader.
//
// Backend contract (per 2026-06-03 backend agent reply):
//   GET /api/flower_tasks/{task_id}/tier1                       -> index JSON
//   GET /api/flower_tasks/{task_id}/tier1/Tier1_result_summary.json
//   GET /api/flower_tasks/{task_id}/tier1/metrics_over_rounds.json
//   GET /api/flower_tasks/{task_id}/tier1/roc_curve.json
//   GET /api/flower_tasks/{task_id}/tier1/pr_curve.json
//   GET /api/flower_tasks/{task_id}/tier1/feature_importance_top10.json
//   GET /api/flower_tasks/{task_id}/tier1/Tier1_result_card.html (text/html)
//
// 404 expected when:
//   - task_id doesn't exist
//   - artifact hasn't been generated (training still running)
//   - artifact doesn't exist for this run path (e.g. FI is LOGREG-only)
//
// This module never throws on 404 / network errors — callers get null and
// fall back to the existing mock card behavior. Once backend's T1+T2
// commits deploy, the same FE code path will start returning real data
// transparently.
//
// Phase F2 dev-0.7.7 — Pipeline Result Card real-data wiring (option F:
// endpoint + iframe only; structured JSON panels deferred until backend
// commits land).

import { API_BASE } from '../config/environment';
import { authenticatedFetch } from './api';

const TIER1_ENDPOINT = (taskId) =>
  `/flower_tasks/${encodeURIComponent(taskId)}/tier1`;

/**
 * GET /api/flower_tasks/{task_id}/tier1 (artifact index).
 * Returns parsed JSON `{ artifacts: [...] }` on 200, null on 404 / any
 * non-2xx, network error, or empty/invalid body.
 */
export const fetchTier1Index = async (taskId) => {
  if (!taskId) return null;
  try {
    const res = await authenticatedFetch(`${API_BASE}${TIER1_ENDPOINT(taskId)}`);
    if (!res.ok) {
      // 404 is expected pre-backend-T2-deploy; don't log noise. Other
      // 4xx/5xx are real but still non-fatal here.
      return null;
    }
    const data = await res.json();
    return data && typeof data === 'object' ? data : null;
  } catch (err) {
    // Network / parse failure → mock fallback. Log but don't escalate.
    console.warn('[tier1Artifacts] fetchTier1Index failed:', err?.message || err);
    return null;
  }
};

/**
 * GET /api/flower_tasks/{task_id}/tier1/Tier1_result_card.html, fetched
 * with Bearer auth, materialized as a blob URL suitable for <iframe src>.
 *
 * Why blob URL instead of direct src=... ? The endpoint requires an
 * Authorization: Bearer header, which a raw <iframe src> cannot send.
 * Fetching with `authenticatedFetch` + converting to blob URL gives the
 * iframe a same-origin (object:) URL that doesn't need its own auth.
 *
 * Returns `{ blobUrl }` on 200, null on 404 / non-2xx / error. The caller
 * is responsible for calling URL.revokeObjectURL(blobUrl) on unmount.
 */
export const fetchTier1CardBlobUrl = async (taskId) => {
  if (!taskId) return null;
  try {
    const res = await authenticatedFetch(
      `${API_BASE}${TIER1_ENDPOINT(taskId)}/Tier1_result_card.html`
    );
    if (!res.ok) return null;
    const blob = await res.blob();
    if (!blob || blob.size === 0) return null;
    return { blobUrl: URL.createObjectURL(blob) };
  } catch (err) {
    console.warn('[tier1Artifacts] fetchTier1CardBlobUrl failed:', err?.message || err);
    return null;
  }
};

/**
 * Map a backend `tier1_summary` payload (T3 inline from
 * GET /api/flower_tasks/{task_id}) into the same "summary panel" shape
 * the inline Model Summary in PipelineResultCardViewer currently
 * consumes from mockPipelineResultCards.
 *
 * Defensive against missing sub-objects — any field that can't be
 * resolved is just omitted / substituted with "—" so the panel still
 * renders cleanly.
 *
 * dev-0.7.8  : tabular binary classification shape.
 * dev-0.7.13 : adds multi-class branch driven by
 *              summary.classification_mode ∈ {"binary", "multiclass"}.
 *              Multi-class differences (per AKS sync §3):
 *                - hero tile labels: "Macro ROC-AUC" / "Macro PR-AUC"
 *                - primary metric:   "macro_f1" (display: "Macro F1-Score")
 *                - recommended_threshold.value === null → "thr = N/A"
 *                - confusion_matrix: {labels[N], matrix[N×N]} instead
 *                                    of flat {TP, FN, FP, TN}
 *                - secondary_metrics: macro_precision / macro_recall /
 *                                     macro_f1 / macro_balanced_accuracy /
 *                                     accuracy (no specificity / recall)
 *              Secondary metrics are surfaced by iterating
 *              Object.entries(mar.secondary_metrics) — never hardcoded.
 */
const fmtNumber = (v, fractionDigits = 4) => {
  if (typeof v !== 'number' || !Number.isFinite(v)) return 'n/a';
  return v.toFixed(fractionDigits);
};

// Strip task-pipeline implementation suffixes off the Flower strategy
// name so the user sees the algorithm family ("FedAvg") rather than the
// backend's plotting/pipeline subclass name. The backend has subclasses
// like FedAvgPlottingSegmentation / FedAvgPlottingClassification etc.
// that exist purely to wire up per-task aggregator hooks — they're the
// same FedAvg algorithm and should read that way in the UI.
const normalizeStrategyName = (raw) => {
  const s = String(raw || 'FedAvg');
  const cleaned = s.replace(/(?:Plotting)?(?:Segmentation|Classification|Tabular)$/i, '');
  return cleaned || 'FedAvg';
};

// Pretty-print metric keys coming from backend (snake_case → Title Case
// with a few hand-tuned overrides for common names).
const METRIC_DISPLAY_NAMES = {
  f1: 'F1-Score',
  macro_f1: 'Macro F1-Score',
  precision: 'Precision',
  recall: 'Recall / Sensitivity',
  specificity: 'Specificity',
  accuracy: 'Accuracy',
  balanced_accuracy: 'Balanced Accuracy',
  macro_precision: 'Macro Precision',
  macro_recall: 'Macro Recall',
  macro_balanced_accuracy: 'Macro Balanced Accuracy',
};
const friendlyMetricName = (key) => {
  if (!key) return '';
  if (METRIC_DISPLAY_NAMES[key]) return METRIC_DISPLAY_NAMES[key];
  return String(key)
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

// Confusion-matrix shape detector. Backend dev-0.7.13 emits one of:
//   binary     : { TP, FN, FP, TN } (flat ints, any casing)
//   multiclass : { labels: [...], matrix: [[...], ...] } (N×N ints)
// Returns a normalized FE-friendly shape, or null if unrecognized.
const deriveConfusionMatrix = (cm) => {
  if (!cm || typeof cm !== 'object') return null;
  if (Array.isArray(cm.matrix) && Array.isArray(cm.labels)) {
    return {
      mode: 'multiclass',
      labels: cm.labels.map((l) => String(l)),
      matrix: cm.matrix.map((row) => (Array.isArray(row) ? row.slice() : [])),
    };
  }
  const get = (k1, k2) => (cm[k1] ?? cm[k2] ?? null);
  const tp = get('TP', 'tp');
  const fn = get('FN', 'fn');
  const fp = get('FP', 'fp');
  const tn = get('TN', 'tn');
  if ([tp, fn, fp, tn].every((v) => typeof v === 'number')) {
    return { mode: 'binary', cells: { tp, fn, fp, tn } };
  }
  return null;
};

// secondary_metrics is a flat {name: value} map from backend. Iterate —
// never hardcode keys — so binary's {precision, recall, specificity,
// accuracy, balanced_accuracy} and multi-class's {macro_precision,
// macro_recall, macro_f1, macro_balanced_accuracy, accuracy} both pass
// through unchanged when backend adds / removes keys.
const deriveSecondaryMetrics = (secMap) => {
  if (!secMap || typeof secMap !== 'object') return null;
  const entries = Object.entries(secMap);
  if (entries.length === 0) return null;
  return entries.map(([key, value]) => ({
    key,
    label: friendlyMetricName(key),
    value:
      typeof value === 'number' && Number.isFinite(value)
        ? value.toFixed(4)
        : (value === null || value === undefined ? 'n/a' : String(value)),
  }));
};

// Multi-key numeric pick: returns the first finite-numeric value found
// across the candidate key list, or null. Used by the seg branch where
// backend field names aren't fully locked yet (best_dice / dice_best /
// best_dice_soft / ... are all plausible). dev-0.7.x — once the seg
// tier1_summary shape stabilizes, can collapse to a single canonical key.
const pickNumeric = (obj, ...keys) => {
  if (!obj || typeof obj !== 'object') return null;
  for (const k of keys) {
    const v = obj[k];
    if (typeof v === 'number' && Number.isFinite(v)) return v;
  }
  return null;
};

// Imaging-segmentation summary derivation.
// Mirrors the mock fixture shape in mockPipelineResultCards.js (the
// segmentation_lidc / segmentation_prostatex entries) so the real-data
// path produces the SAME panel layout users have already seen in the
// prototype: hero KPI tiles for Best Dice (soft) / Final Dice (hard) /
// Final IoU (hard), federated meta line, "Final loss: X (BCE+Dice)"
// threshold-note style line, and seg-specific "includes" bullets.
//
// Backend tier1_summary key names for seg aren't fully locked across
// dev-0.7.x; we read defensively via pickNumeric() over plausible
// aliases and fall back to "—" when nothing matches. After the first
// real seg run the user can paste the captured [FX-DEBUG-TIER1] dump
// back so we can collapse to the canonical names.
const deriveSegSummary = (tier1Summary, fallbackDatasetName) => {
  const sum = tier1Summary.summary || {};
  const meta = tier1Summary.run_metadata || {};

  const bestModel = String(sum.best_model || meta.model_type || 'UNET').toUpperCase();

  // Defensive field reads — try canonical names from the mock fixture
  // first, then a few common variants seen across Flower seg pipelines.
  const bestDice  = pickNumeric(sum, 'best_dice', 'best_dice_soft', 'dice_soft_best', 'dice_best');
  const finalDice = pickNumeric(sum, 'final_dice', 'final_dice_hard', 'dice_final', 'dice_hard_final');
  const finalIoU  = pickNumeric(sum, 'final_iou',  'final_iou_hard',  'iou_final',  'iou_hard_final');
  const finalLoss = pickNumeric(sum, 'final_loss', 'loss_final', 'final_loss_value');
  const bestRound = pickNumeric(sum, 'best_round', 'best_dice_round', 'best_round_number');

  const lossName  = sum.loss_name || meta.loss_name || 'BCE+Dice';
  const strategy  = normalizeStrategyName(meta.strategy);
  const numClients = meta.num_clients;
  const numRounds  = meta.num_rounds;
  const localEpochs = meta.local_epochs ?? meta.localEpochs;

  const title = `Imaging Segmentation · ${bestModel}${strategy ? ` · ${strategy}` : ''}`;
  const subtitleParts = [];
  if (numClients != null) subtitleParts.push(`${numClients} clients`);
  if (numRounds  != null) subtitleParts.push(`${numRounds} rounds`);
  if (bestRound  != null) subtitleParts.push(`best round ${bestRound}`);
  const subtitle = subtitleParts.join(' · ');

  // Hero tiles mirror the mock fixture exactly: Model + Best Dice (primary,
  // hinted with the round at which it was hit) + Final Dice + Final IoU.
  const heroMetrics = [
    { label: 'Model', value: bestModel, text: true },
    {
      label: 'Best Dice (soft)',
      value: fmtNumber(bestDice, 4),
      primary: true,
      hint: bestRound != null ? `@ round ${bestRound}` : 'Selection metric',
    },
    { label: 'Final Dice (hard)', value: fmtNumber(finalDice, 4) },
    { label: 'Final IoU (hard)',  value: fmtNumber(finalIoU, 4) },
  ];

  const federatedMeta = {
    strategy,
    rounds: numRounds ?? 'n/a',
    clients: numClients ?? 'n/a',
    localEpochs: localEpochs ?? 'n/a',
  };

  // For seg there's no "recommended threshold" — replace that line with
  // the final-loss line (matches the mock fixture).
  const thresholdNote = finalLoss != null
    ? `Final loss: ${fmtNumber(finalLoss, 4)} (${lossName})`
    : '';

  const includes = [
    'Loss over rounds, Dice (soft / hard) and IoU (hard) over rounds plots',
    'Sample mask examples (dev preview)',
    'Run metadata block',
  ];

  return {
    title,
    dataset: fallbackDatasetName || meta.dataset_name || 'n/a',
    subtitle,
    heroMetrics,
    federatedMeta,
    thresholdNote,
    labelDef: '',
    includes,
    // Mark the mode for downstream UI gates. Both the FE-side CM panel
    // and the secondary-metrics table are gated on truthy values, so
    // explicitly returning null for both keeps them hidden under seg.
    classificationMode: 'segmentation',
    confusionMatrix: null,
    secondaryMetrics: null,
    // dev-0.7.33 — surface the sample_previews metadata so the viewer
    // can fetch best/median/worst PNG blobs and render them. Null on
    // payloads without the field (e.g. classification runs).
    samplePreviewsMeta: tier1Summary.sample_previews || null,
    _raw: tier1Summary,
  };
};

export const deriveSummaryFromTier1 = (tier1Summary, fallbackDatasetName) => {
  if (!tier1Summary || typeof tier1Summary !== 'object') return null;

  // dev-0.7.x diagnostic: dump the FIRST tier1_summary of each session
  // so we can lock the seg / cls field names against the actual backend
  // shape. Throttled to once per page load via window flag so noisy
  // polling doesn't spam the console.
  try {
    if (typeof window !== 'undefined' && !window.__FX_TIER1_DUMPED) {
      window.__FX_TIER1_DUMPED = true;
      console.log('[FX-DEBUG-TIER1] first tier1_summary shape:',
        JSON.stringify(tier1Summary));
    }
  } catch (_) {}

  // ── Segmentation branch (dev-0.7.21+) ────────────────────────────────
  // Detect imaging segmentation from any of the canonical fields backend
  // may set: top-level `task`, run_metadata.task, dataset_type, or a
  // dataset_ref pattern. Falls through to classification on no match.
  const taskTop  = String(tier1Summary.task || '').toLowerCase();
  const taskMeta = String(tier1Summary.run_metadata?.task || '').toLowerCase();
  const dsType   = String(tier1Summary.dataset_type
                          || tier1Summary.run_metadata?.dataset_type
                          || '').toLowerCase();
  const dsRef    = String(tier1Summary.run_metadata?.dataset_ref || '').toLowerCase();
  const isSegmentation =
    taskTop === 'imaging_segmentation' || taskTop === 'segmentation' ||
    taskMeta === 'imaging_segmentation' || taskMeta === 'segmentation' ||
    dsType === 'imaging_segmentation' ||
    /(^|_)seg(_|$|_v\d)/.test(dsRef);

  if (isSegmentation) {
    return deriveSegSummary(tier1Summary, fallbackDatasetName);
  }

  const sum = tier1Summary.summary || {};
  const mar = tier1Summary.metrics_at_recommended_threshold || {};
  const meta = tier1Summary.run_metadata || {};
  const labelDef = meta.label_definition || {};

  // ── dev-0.7.13: classification_mode branch ──────────────────────────
  // Default to "binary" if backend omits the field (older runs / mock).
  const classificationMode = sum.classification_mode === 'multiclass'
    ? 'multiclass'
    : 'binary';
  const isMulti = classificationMode === 'multiclass';

  const bestModel = String(sum.best_model || meta.model_type || 'unknown').toUpperCase();
  const rocAuc = sum.roc_auc;
  const prAuc = sum.pr_auc;
  const primaryMetric = mar.primary_metric || {};
  // Pull backend's literal metric name (e.g. "f1" / "macro_f1") and
  // render with the friendly map. Falls back to a snake_case-aware
  // title-caser for any future metric we haven't hand-tuned.
  const primaryRawName = primaryMetric.name || (isMulti ? 'macro_f1' : 'f1');
  const primaryLabel = friendlyMetricName(primaryRawName);
  const primaryValue = primaryMetric.value;

  const recThr = sum.recommended_threshold || {};
  const thrName = recThr.name || 'recommended';
  // dev-0.7.13: multi-class always reports recommended_threshold.value
  // as null (no global threshold over an N-class softmax). Render as
  // "N/A" rather than the legacy "—" so the user sees an explicit reason.
  const thrIsNull = recThr.value === null || recThr.value === undefined;
  const thrValueStr = thrIsNull ? 'N/A' : fmtNumber(recThr.value, 4);

  const taskKind = tier1Summary.task || 'tabular_classification';
  const niceTaskName = taskKind === 'tabular_classification'
    ? `Tabular ${isMulti ? 'Multi-class ' : ''}Classification`
    : taskKind.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  const strategy = normalizeStrategyName(meta.strategy);
  const title = `${niceTaskName} · ${bestModel}${strategy ? ` · ${strategy}` : ''}`;

  const nFeatures = meta.n_features;
  const numClasses = meta.num_classes;
  const subtitle = nFeatures != null && numClasses != null
    ? `${nFeatures} features · ${numClasses} classes`
    : (nFeatures != null ? `${nFeatures} features` : '');

  const numClients = meta.num_clients;
  const numRounds = meta.num_rounds;
  const localEpochs = meta.local_epochs ?? meta.localEpochs;

  // ── KPI hero tiles ─────────────────────────────────────────────────
  // Binary keeps the original labels ("ROC-AUC" / "PR-AUC" / "F1-Score").
  // Multi-class swaps to "Macro ROC-AUC" / "Macro PR-AUC" / "Macro
  // F1-Score" and the threshold hint becomes explicit "N/A" because the
  // value is genuinely undefined for multi-class softmax outputs.
  const rocAucLabel = isMulti ? 'Macro ROC-AUC' : 'ROC-AUC';
  const prAucLabel = isMulti ? 'Macro PR-AUC' : 'PR-AUC';
  const primaryHint = thrIsNull
    ? 'no global threshold (multi-class)'
    : `at ${thrName} = ${thrValueStr}`;

  const heroMetrics = [
    { label: 'Model', value: bestModel, text: true },
    { label: rocAucLabel, value: fmtNumber(rocAuc, 4) },
    {
      label: prAucLabel,
      value: fmtNumber(prAuc, 4),
      primary: true,
      hint: 'Selection metric',
    },
    {
      label: primaryLabel,
      value: fmtNumber(primaryValue, 4),
      hint: primaryHint,
    },
  ];

  const federatedMeta = {
    strategy,
    rounds: numRounds ?? 'n/a',
    clients: numClients ?? 'n/a',
    localEpochs: localEpochs ?? 'n/a',
  };

  // Threshold note: explicit "thr = N/A" for multi-class so the user
  // knows it's not a missing-data symptom.
  const thresholdNote = `${thrName} · thr = ${thrValueStr}`;

  const labelDefStr = (() => {
    if (!labelDef || typeof labelDef !== 'object') return '';
    const parts = [];
    if (labelDef.positive_label) parts.push(`Positive: ${labelDef.positive_label}`);
    if (labelDef.negative_label) parts.push(`Negative: ${labelDef.negative_label}`);
    if (labelDef.positive_count != null) parts.push(`${labelDef.positive_count} pos`);
    if (labelDef.negative_count != null) parts.push(`${labelDef.negative_count} neg`);
    if (labelDef.positive_pct) parts.push(`${labelDef.positive_pct} positive`);
    return parts.join(' · ');
  })();

  // `includes` is educational text — what the full HTML card contains.
  // FI bullet is LOGREG-only (dev-0.7.8 contract).
  const includes = isMulti
    ? [
        `Confusion matrix (${numClasses ?? 'N'}×${numClasses ?? 'N'}) + macro per-class metrics at the recommended operating point`,
        'ROC and Precision-Recall curves (macro-OvR averaged, with micro-AUC overlay)',
        bestModel === 'LOGREG'
          ? 'Metrics over rounds, feature importance (top 10)'
          : 'Metrics over rounds',
        'Run metadata block',
      ]
    : [
        'Confusion matrix + metrics at recommended threshold (F1, precision, recall, specificity, balanced accuracy)',
        'ROC curve and Precision-Recall curve (global)',
        bestModel === 'LOGREG'
          ? 'Metrics over rounds, feature importance (top 10)'
          : 'Metrics over rounds',
        'Run metadata block',
      ];

  // ── New FE-side panel data: CM + secondary metrics ─────────────────
  // Both come straight from tier1_summary.metrics_at_recommended_threshold.
  // PipelineResultCardViewer renders these natively (in addition to the
  // iframe HTML card opened by "View Full Result Card"), so the user
  // sees the key structured numbers without opening the modal.
  const confusionMatrix = deriveConfusionMatrix(mar.confusion_matrix);
  const secondaryMetrics = deriveSecondaryMetrics(mar.secondary_metrics);

  return {
    title,
    dataset: fallbackDatasetName || meta.dataset_name || 'n/a',
    subtitle,
    heroMetrics,
    federatedMeta,
    thresholdNote,
    labelDef: labelDefStr,
    includes,
    // dev-0.7.13 additions:
    classificationMode,
    confusionMatrix,
    secondaryMetrics,
    // dev-0.7.33 — also surface here defensively. Per AKS sync §七,
    // classification tasks don't currently emit sample_previews, but
    // forwarding the field if backend ever adds one means the viewer
    // doesn't need a second wiring change.
    samplePreviewsMeta: tier1Summary.sample_previews || null,
    // Carry the full payload too, in case future panels want to drill in.
    _raw: tier1Summary,
  };
};

/**
 * dev-0.7.33 — fallback fetch of Tier1_result_summary.json directly,
 * for cases where backend's inline tier1_summary in the polling
 * response (T3 fast-path) doesn't carry the sample_previews block but
 * the standalone JSON file does. Returns the parsed JSON or null on
 * any 404 / parse error / non-2xx.
 *
 * Goes through authenticatedFetch (same auth helper the page already
 * uses for /api/training_summary / /api/flower_tasks/<id> etc.), so
 * CORS preflight is handled identically to the page's known-working
 * polling fetches. (Direct console fetches from devtools have hit
 * CORS preflight 404s; this util uses the same auth context.)
 */
export const fetchTier1SummaryJson = async (taskId) => {
  if (!taskId) return null;
  try {
    const res = await authenticatedFetch(
      `${API_BASE}${TIER1_ENDPOINT(taskId)}/Tier1_result_summary.json`
    );
    if (!res.ok) return null;
    const body = await res.json();
    return body && typeof body === 'object' ? body : null;
  } catch (err) {
    console.warn('[tier1Artifacts] fetchTier1SummaryJson failed:', err?.message || err);
    return null;
  }
};

/**
 * dev-0.7.33 — fetch the best/median/worst sample-preview PNGs for a
 * segmentation Tier1 result.
 *
 * Backend writes 3 servable flat-named PNGs:
 *   /api/flower_tasks/<task_id>/tier1/sample_preview_best.png
 *   /api/flower_tasks/<task_id>/tier1/sample_preview_median.png
 *   /api/flower_tasks/<task_id>/tier1/sample_preview_worst.png
 *
 * Each path is surfaced via Tier1_result_summary.json's
 * `sample_previews.tiers.<tier>.image_path` field (a relative filename).
 * We can't put these in iframe-modal `<img>` tags because (a) Flask
 * requires Bearer auth and (b) the modal HTML loads via blob URL so
 * relative URLs don't resolve back to the API. Same workaround as the
 * full result-card HTML: authenticatedFetch → blob → URL.createObjectURL,
 * then use the blob URL as the `<img src>`.
 *
 * Returns an object keyed by tier ("best" / "median" / "worst") with:
 *   {
 *     status:   "ok" | "metadata_only" | "failed"   // from backend
 *     score:    number | undefined,                 // per-tier metric
 *     sampleId: string | undefined,                 // sample identifier
 *     blobUrl:  string | null,                      // null when not ok
 *   }
 * Returns null when input lacks sample_previews entirely.
 *
 * CALLER MUST call URL.revokeObjectURL(blobUrl) on each non-null blobUrl
 * when the result is no longer needed (component unmount, taskId change,
 * etc.) — otherwise the browser leaks the blob.
 */
export const fetchSamplePreviewBlobUrls = async (taskId, samplePreviewsMeta) => {
  if (!taskId || !samplePreviewsMeta || typeof samplePreviewsMeta !== 'object') {
    return null;
  }
  const tiers = samplePreviewsMeta.tiers;
  if (!tiers || typeof tiers !== 'object') return null;

  const result = {};
  for (const [tier, meta] of Object.entries(tiers)) {
    const baseEntry = {
      status: meta?.image_status || 'failed',
      score: meta?.score,
      sampleId: meta?.sample_id,
      blobUrl: null,
    };
    // Skip the network fetch when backend already indicated nothing is
    // there — preserves the metadata so caller can render the right
    // placeholder ("metadata-only run", "failed", etc.).
    if (!meta || meta.image_status !== 'ok' || !meta.image_path) {
      result[tier] = baseEntry;
      continue;
    }
    try {
      const res = await authenticatedFetch(
        `${API_BASE}${TIER1_ENDPOINT(taskId)}/${meta.image_path}`
      );
      if (!res.ok) {
        result[tier] = baseEntry;
        continue;
      }
      const blob = await res.blob();
      if (!blob || blob.size === 0) {
        result[tier] = baseEntry;
        continue;
      }
      result[tier] = {
        status: 'ok',
        score: meta.score,
        sampleId: meta.sample_id,
        blobUrl: URL.createObjectURL(blob),
      };
    } catch (err) {
      console.warn(
        `[tier1Artifacts] fetchSamplePreviewBlobUrls(${tier}) failed:`,
        err?.message || err
      );
      result[tier] = baseEntry;
    }
  }
  return result;
};

/**
 * Convenience: given a task_id, try to load just enough to know whether
 * the real Tier1 result card is available. Returns:
 *   { available: true,  cardBlobUrl: "blob:..." }   when ready
 *   { available: false }                            when 404 / not ready
 *
 * On `available: true`, the caller should:
 *   - swap iframe src to `cardBlobUrl`
 *   - call URL.revokeObjectURL(cardBlobUrl) on unmount or taskId change
 */
export const tryLoadTier1Card = async (taskId) => {
  if (!taskId) return { available: false };
  const index = await fetchTier1Index(taskId);
  // Defensive: only proceed if index lists Tier1_result_card.html. If
  // backend ever changes the artifact set, this protects us from a
  // dangling fetch attempt.
  const hasCard =
    index &&
    Array.isArray(index.artifacts) &&
    index.artifacts.some((a) => a && a.name === 'Tier1_result_card.html');
  if (!hasCard) return { available: false };
  const card = await fetchTier1CardBlobUrl(taskId);
  if (!card || !card.blobUrl) return { available: false };
  return { available: true, cardBlobUrl: card.blobUrl };
};
